import { useCallback, useMemo, useState } from 'react';
import { type TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from './store/index';

// Typed hooks
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Auth hooks - Updated to use Clerk auth
export const useAuth = () => {
  const { ezhoaUser, isLoading, error } = useAppSelector((state) => state.clerkAuth);
  return {
    user: ezhoaUser,
    isAuthenticated: !!ezhoaUser,
    loading: isLoading,
    error,
  };
};

export const useCurrentUser = () => {
  const user = useAppSelector((state) => state.clerkAuth.ezhoaUser);
  return user;
};

export const useIsAdmin = () => {
  const user = useCurrentUser();
  return user?.role === 'admin';
};

export const useIsVendor = () => {
  const user = useCurrentUser();
  return user?.role === 'vendor';
};

export const useIsHomeowner = () => {
  const user = useCurrentUser();
  return user?.role === 'homeowner';
};

// Permission hooks
export const useHasPermission = (permission: string) => {
  const user = useCurrentUser();

  return useMemo(() => {
    if (!user) return false;
    if (user.role === 'admin') return true; // Admins have all permissions
    return user.permissions?.includes(permission) || false;
  }, [user, permission]);
};

// UI hooks
export const useModal = (modalType?: string) => {
  const dispatch = useAppDispatch();
  const activeModal = useAppSelector((state) => state.ui.activeModal);
  const modalData = useAppSelector((state) => state.ui.modalData);

  const isOpen = modalType ? activeModal === modalType : !!activeModal;

  const open = useCallback(
    (data?: unknown) => {
      if (modalType) {
        dispatch({ type: 'ui/openModal', payload: { type: modalType, data } });
      }
    },
    [dispatch, modalType]
  );

  const close = useCallback(() => {
    dispatch({ type: 'ui/closeModal' });
  }, [dispatch]);

  return { isOpen, open, close, data: modalData };
};

export const useToast = () => {
  const dispatch = useAppDispatch();

  return useCallback(
    (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
      dispatch({
        type: 'ui/showToast',
        payload: { message, type },
      });
    },
    [dispatch]
  );
};

// Loading state hooks
export const useLoadingState = (key: string) => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector((state) => state.ui.loadingStates[key] || false);

  const setLoading = useCallback(
    (loading: boolean) => {
      dispatch({
        type: 'ui/setLoadingState',
        payload: { key, loading },
      });
    },
    [dispatch, key]
  );

  return [isLoading, setLoading] as const;
};

// Async action hook with loading state
export const useAsyncAction = <T extends (...args: unknown[]) => unknown>(
  action: T,
  deps: unknown[] = []
) => {
  const dispatch = useAppDispatch();
  const [isLoading, setLoading] = useLoadingState(action.name || 'async');

  const execute = useCallback(
    async (...args: Parameters<T>) => {
      setLoading(true);
      try {
        const result = await dispatch(action(...args));
        if ('error' in result) {
          throw result.error;
        }
        return result;
      } finally {
        setLoading(false);
      }
    },
    [dispatch, action, setLoading, ...deps]
  );

  return { execute, isLoading };
};

// Pagination hook
export const usePagination = (initialPage = 1, initialLimit = 20) => {
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);

  const nextPage = useCallback(() => setPage((p) => p + 1), []);
  const prevPage = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);
  const goToPage = useCallback((p: number) => setPage(Math.max(1, p)), []);
  const changeLimit = useCallback((l: number) => {
    setLimit(l);
    setPage(1); // Reset to first page when limit changes
  }, []);

  return {
    page,
    limit,
    nextPage,
    prevPage,
    goToPage,
    changeLimit,
  };
};
